import { useEffect, useState, type ReactNode } from 'react'
import './PhoneMockup.css'

/** Cotes de référence du châssis. Toute autre taille est obtenue par mise à l'échelle. */
const FRAME_WIDTH = 300
const FRAME_HEIGHT = 623
/** Largeur maximale du châssis sur petit écran. */
const NARROW_WIDTH = 240

export interface PhoneMockupProps {
  /** Contenu affiché plein cadre dans l'écran — une capture s'y substitue sans toucher au châssis. */
  children: ReactNode
  /** Légende sous l'appareil. */
  caption?: ReactNode
  /** Largeur du châssis en px. Toutes les cotes suivent proportionnellement. */
  deviceWidth?: number
  /** Description de l'illustration pour les lecteurs d'écran. */
  label?: string
}

function useNarrowViewport() {
  const [narrow, setNarrow] = useState(false)

  useEffect(() => {
    const query = window.matchMedia('(max-width: 479px)')
    const sync = () => setNarrow(query.matches)
    sync()
    query.addEventListener('change', sync)
    return () => query.removeEventListener('change', sync)
  }, [])

  return narrow
}

/** Châssis d'iPhone. Le contenu passé en `children` occupe l'écran entier ; le chrome iOS se superpose. */
export function PhoneMockup({
  children,
  caption,
  deviceWidth = FRAME_WIDTH,
  label = "Illustration d'un iPhone affichant le parcours de réservation en ligne",
}: PhoneMockupProps) {
  const narrow = useNarrowViewport()
  const width = narrow ? Math.min(deviceWidth, NARROW_WIDTH) : deviceWidth
  const scale = width / FRAME_WIDTH

  return (
    <div className="azoya-phone-mockup-wrap">
      <div
        className="azoya-phone-mockup-scaler"
        style={{ width: Math.round(FRAME_WIDTH * scale), height: Math.round(FRAME_HEIGHT * scale) }}
      >
        <div className="azoya-phone-mockup" role="img" aria-label={label} style={{ transform: `scale(${scale})` }}>
          <span className="azoya-phone-mockup-button azoya-phone-mockup-button-action" aria-hidden="true" />
          <span className="azoya-phone-mockup-button azoya-phone-mockup-button-volume-up" aria-hidden="true" />
          <span className="azoya-phone-mockup-button azoya-phone-mockup-button-volume-down" aria-hidden="true" />
          <span className="azoya-phone-mockup-button azoya-phone-mockup-button-side" aria-hidden="true" />

          <div className="azoya-phone-mockup-screen">
            {children}

            <div className="azoya-phone-mockup-statusbar" aria-hidden="true">
              <span className="azoya-phone-mockup-time">9:41</span>
              <span className="azoya-phone-mockup-status-icons">
                <svg width="17" height="12" viewBox="0 0 17 12" fill="#264653">
                  <rect x="0" y="8" width="3" height="4" rx="1" />
                  <rect x="4.5" y="6" width="3" height="6" rx="1" />
                  <rect x="9" y="3.5" width="3" height="8.5" rx="1" />
                  <rect x="13.5" y="1" width="3" height="11" rx="1" />
                </svg>
                <svg
                  width="16"
                  height="12"
                  viewBox="0 0 16 12"
                  fill="none"
                  stroke="#264653"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                >
                  <path d="M1.2 4.1a10 10 0 0 1 13.6 0" />
                  <path d="M3.8 6.9a6.2 6.2 0 0 1 8.4 0" />
                  <path d="M6.4 9.6a2.5 2.5 0 0 1 3.2 0" />
                </svg>
                <svg width="24" height="12" viewBox="0 0 24 12" fill="none">
                  <rect
                    x="0.6"
                    y="0.6"
                    width="20"
                    height="10.8"
                    rx="3.2"
                    stroke="#264653"
                    strokeOpacity="0.45"
                    strokeWidth="1.2"
                  />
                  <rect x="2.2" y="2.2" width="14.7" height="7.6" rx="2" fill="#264653" />
                  <path d="M22.2 4.2v3.6a2 2 0 0 0 0-3.6z" fill="#264653" fillOpacity="0.45" />
                </svg>
              </span>
            </div>

            <div className="azoya-phone-mockup-island" aria-hidden="true" />
            <div className="azoya-phone-mockup-home" aria-hidden="true" />
          </div>
        </div>
      </div>
      {caption && <p className="azoya-phone-mockup-caption">{caption}</p>}
    </div>
  )
}
